import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import ProfileView from '../profile/ProfileView';
import { useAuthStore } from '../../store/authStore';
import { useMessageStore } from '../../store/messageStore';
import { useFriendStore } from '../../store/friendStore';
import { useServerStore } from '../../store/serverStore';
import ContextMenu, { ContextMenuItem } from '../ui/ContextMenu';
import { User, MessageSquare, UserPlus, Copy, Plus, Users, Check, UserMinus, ShieldBan, ArrowRightLeft } from 'lucide-react';
import { useDeveloperMode } from '../../lib/developerMode';
import { Permissions, computeEffectivePermissions, hasPermission } from '../../lib/permissions';

interface UserProfileLinkProps {
  userId: string;
  children: React.ReactNode;
  className?: string;
  preventClick?: boolean;
}

const UserProfileLink: React.FC<UserProfileLinkProps> = ({ userId, children, className, preventClick = false }) => {
  const [showProfile, setShowProfile] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [showTransferConfirm, setShowTransferConfirm] = useState(false);
  const [transferConfirmName, setTransferConfirmName] = useState('');
  const { user } = useAuthStore();
  const { setCurrentDmUser } = useMessageStore();
  const { sendFriendRequest } = useFriendStore();
  const { currentServer, currentChannel, members, roles, fetchServerMembers, fetchServerRoles, fetchUserServers } = useServerStore();
  const developerMode = useDeveloperMode();
  const [applicationCommands, setApplicationCommands] = useState<any[]>([]);
  
  const isCurrentUser = user?.id === userId;
  const serverMember = currentServer ? members.find((member: any) => member.user_id === userId) : null;
  const currentMember = currentServer && user ? members.find((member: any) => member.user_id === user.id) : null;
  const isServerOwner = Boolean(currentServer && user?.id === currentServer.owner_id);
  const isTargetOwner = Boolean(currentServer && userId === currentServer.owner_id);
  const isBot = Boolean(serverMember?.bot || serverMember?.user?.bot || serverMember?.user?.is_system);
  const targetName = serverMember?.user?.display_name || serverMember?.user?.username || 'this member';
  const effectivePerms = computeEffectivePermissions(isServerOwner, ((currentMember as any)?.roles || []) as { permissions: number }[]);
  const canManageRoles = Boolean(currentServer && hasPermission(effectivePerms, Permissions.MANAGE_ROLES));
  const canKick = Boolean(currentServer && hasPermission(effectivePerms, Permissions.KICK_MEMBERS));
  const canBan = Boolean(currentServer && hasPermission(effectivePerms, Permissions.BAN_MEMBERS));

  useEffect(() => {
    if (!currentChannel?.id) {
      setApplicationCommands([]);
      return;
    }
    let cancelled = false;
    useMessageStore.getState().messagesAPI.getChannelApplicationCommands(currentChannel.id)
      .then((commands: any[]) => {
        if (!cancelled) setApplicationCommands(Array.isArray(commands) ? commands : []);
      })
      .catch(() => {
        if (!cancelled) setApplicationCommands([]);
      });
    return () => {
      cancelled = true;
    };
  }, [currentChannel?.id]);

  const handleProfileClick = (e: React.MouseEvent) => {
    if (preventClick) return;
    e.preventDefault();
    e.stopPropagation();
    setShowProfile(true);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const userCommandsByApp = applicationCommands
    .filter((command) => command.type === 2)
    .reduce<Record<string, any[]>>((acc, command) => {
      const appId = command.application_id || command.application?.id || 'application';
      acc[appId] = [...(acc[appId] || []), command];
      return acc;
    }, {});

  const applicationItems: ContextMenuItem[] = Object.keys(userCommandsByApp).length > 0 ? [{
    label: 'Applications',
    icon: <Plus className="h-4 w-4" />,
    onClick: () => {},
    children: Object.entries(userCommandsByApp).map(([appId, commands]) => {
      const application = commands[0]?.application || {};
      return {
        label: application.name || 'Application',
        avatar: application.avatar,
        onClick: () => {},
        children: commands.map((command) => ({
          label: command.name,
          icon: <Plus className="h-4 w-4" />,
          onClick: () => {
            if (!currentChannel?.id) return;
            void useMessageStore.getState().messagesAPI.createChannelInteraction(currentChannel.id, {
              application_id: command.application_id || appId,
              command_id: command.id,
              type: 2,
              data: {
                id: command.id,
                name: command.name,
                type: command.type,
                target_id: userId,
                resolved: { users: { [userId]: { id: userId, username: 'user' } } },
              },
            });
          },
        })),
      };
    }),
  }] : [];

  const refreshServerMembers = async () => {
    if (!currentServer?.id) return;
    await Promise.all([
      fetchServerMembers(currentServer.id, { silent: true }),
      fetchServerRoles(currentServer.id, { silent: true }).catch(() => null),
    ]);
  };

  const toggleRole = async (roleId: string, hasRole: boolean) => {
    if (!currentServer?.id) return;
    const token = localStorage.getItem('access_token');
    if (hasRole) {
      await axios.delete(`${import.meta.env.VITE_API_URL}/servers/${currentServer.id}/members/${userId}/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } else {
      await axios.post(`${import.meta.env.VITE_API_URL}/servers/${currentServer.id}/members/${userId}/roles/${roleId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }
    await refreshServerMembers();
  };

  const kickMember = async () => {
    if (!currentServer?.id) return;
    const token = localStorage.getItem('access_token');
    await axios.delete(`${import.meta.env.VITE_API_URL}/servers/${currentServer.id}/members/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await refreshServerMembers();
  };

  const banMember = async () => {
    if (!currentServer?.id) return;
    const token = localStorage.getItem('access_token');
    await axios.post(`${import.meta.env.VITE_API_URL}/servers/${currentServer.id}/bans`, { userId }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await refreshServerMembers();
  };

  const transferOwnership = async () => {
    if (!currentServer?.id) return;
    const token = localStorage.getItem('access_token');
    await axios.post(`${import.meta.env.VITE_API_URL}/servers/${currentServer.id}/transfer-ownership`, { newOwnerId: userId }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setShowTransferConfirm(false);
    setTransferConfirmName('');
    await Promise.all([refreshServerMembers(), fetchUserServers()]);
  };

  const memberRoleIds = new Set(((serverMember as any)?.roles || []).map((role: any) => role.id));
  const assignableRoles = roles
    .filter((role: any) => !role.is_everyone)
    .sort((a: any, b: any) => (b.position || 0) - (a.position || 0));
  const canToggleRolesForTarget = Boolean(currentServer && serverMember && canManageRoles && ((!isCurrentUser && !isTargetOwner) || (isTargetOwner && isServerOwner)));
  const canUseModerationActions = Boolean(currentServer && serverMember && !isCurrentUser && !isTargetOwner);
  const moderationItems: ContextMenuItem[] = currentServer && serverMember && (canToggleRolesForTarget || canUseModerationActions) ? [
    {
      label: 'Roles',
      icon: <Users className="h-4 w-4" />,
      onClick: () => {},
      hidden: !canToggleRolesForTarget,
      separator: true,
      children: assignableRoles.map((role: any) => {
        const hasRole = memberRoleIds.has(role.id);
        return {
          label: role.name,
          icon: hasRole ? <Check className="h-4 w-4" /> : <span className="block h-3 w-3 rounded-full" style={{ backgroundColor: role.color || '#99AAB5' }} />,
          onClick: () => void toggleRole(role.id, hasRole),
        };
      }),
    },
    {
      label: isBot ? 'Remove App' : 'Kick',
      icon: <UserMinus className="h-4 w-4" />,
      onClick: () => void kickMember(),
      hidden: !canUseModerationActions || !canKick,
    },
    {
      label: 'Ban',
      icon: <ShieldBan className="h-4 w-4" />,
      onClick: () => void banMember(),
      danger: true,
      hidden: !canUseModerationActions || !canBan || isBot,
    },
    {
      label: 'Transfer Ownership',
      icon: <ArrowRightLeft className="h-4 w-4" />,
      onClick: () => setShowTransferConfirm(true),
      hidden: !canUseModerationActions || !isServerOwner || isBot,
      separator: true,
    },
  ] : [];

  const contextMenuItems: ContextMenuItem[] = [
    ...applicationItems,
    {
      label: 'Profile',
      icon: <User className="h-4 w-4" />,
      onClick: () => setShowProfile(true),
      separator: applicationItems.length > 0,
    },
    {
      label: 'Message',
      icon: <MessageSquare className="h-4 w-4" />,
      onClick: () => setCurrentDmUser(userId),
      hidden: isCurrentUser,
    },
    {
      label: 'Add Friend',
      icon: <UserPlus className="h-4 w-4" />,
      onClick: () => sendFriendRequest(userId),
      hidden: isCurrentUser,
    },
    {
      label: 'Copy User ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(userId),
      separator: true,
      hidden: !developerMode,
    },
    ...moderationItems,
  ];

  return (
    <>
      <span 
        className={`cursor-pointer ${className || ''}`}
        onClick={handleProfileClick}
        onContextMenu={handleContextMenu}
      >
        {children}
      </span>
      
      {showProfile && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[200]" onClick={() => setShowProfile(false)}>
          <div className="relative w-80" onClick={e => e.stopPropagation()}>
            <ProfileView 
              userId={userId}
              isCurrentUser={isCurrentUser}
              onClose={() => setShowProfile(false)}
            />
          </div>
        </div>
      , document.body)}

      <ContextMenu
        items={contextMenuItems}
        position={contextMenu}
        onClose={() => setContextMenu(null)}
      />

      {showTransferConfirm && currentServer && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200]" onClick={() => { setShowTransferConfirm(false); setTransferConfirmName(''); }}>
          <div className="bg-bg-primary border border-border-color rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-text-primary mb-2">Transfer Ownership</h3>
            <p className="text-sm text-text-secondary mb-4">
              Transfer ownership of <span className="font-semibold text-text-primary">{currentServer.name}</span> to <span className="font-semibold text-text-primary">{targetName}</span>. This action is <span className="text-error font-semibold">irreversible</span>.
            </p>
            <p className="text-xs text-text-muted mb-2">Type the server name to confirm:</p>
            <input
              type="text"
              value={transferConfirmName}
              onChange={(e) => setTransferConfirmName(e.target.value)}
              placeholder={currentServer.name}
              className="settings-input mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowTransferConfirm(false); setTransferConfirmName(''); }}
                className="px-4 py-2 text-sm rounded-lg text-text-secondary hover:text-text-primary hover:bg-highlight/50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => void transferOwnership()}
                disabled={transferConfirmName !== currentServer.name}
                className="px-4 py-2 text-sm rounded-lg bg-error text-white hover:bg-error/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Transfer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default UserProfileLink;
